import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Sparkles, Wand2, Settings2, GripVertical } from "lucide-react";
import { useFields } from "@/context/FieldsContext";
import { makeField, labelFor } from "@/lib/fields";

export const FieldsSettings = ({ open, onOpenChange }) => {
  const { config, setConfig } = useFields();

  const setAuto = (v) => setConfig((prev) => ({ ...prev, auto_fields: v }));

  const updateField = (idx, patch) =>
    setConfig((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));

  const addField = () =>
    setConfig((prev) => ({ ...prev, fields: [...prev.fields, makeField()] }));

  const removeField = (idx) =>
    setConfig((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="fields-settings-panel"
        side="right"
        className="bg-card border-border w-full sm:max-w-lg p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-4 border-b border-border">
          <SheetTitle className="font-heading text-slate-50 flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" /> Fields & AI Criteria
          </SheetTitle>
          <p className="text-xs text-muted-foreground">
            Define custom fields that both label your table and tell the AI what to capture. Changes apply instantly.
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Automatic fields master toggle */}
          <div className="rounded-xl border border-border bg-secondary/20 p-4 flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent">
                <Wand2 className="h-4 w-4" />
              </span>
              <div>
                <div className="text-sm font-semibold text-slate-100">Automatic fields</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Let the AI auto-detect general details (name, a short description and any visible text).
                  Turn off to capture <span className="text-slate-200">only</span> your defined fields below.
                </p>
              </div>
            </div>
            <Switch
              data-testid="auto-fields-switch"
              checked={config.auto_fields}
              onCheckedChange={setAuto}
            />
          </div>

          {/* Custom fields */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Custom Fields ({config.fields.length})
              </span>
              <Button
                data-testid="add-field-button"
                size="sm"
                variant="outline"
                onClick={addField}
                className="gap-1.5 border-border bg-secondary/40 hover:bg-secondary h-8"
              >
                <Plus className="h-3.5 w-3.5" /> Add field
              </Button>
            </div>

            {config.fields.length === 0 && (
              <div className="rounded-xl border border-dashed border-border p-6 text-center">
                <Sparkles className="mx-auto h-6 w-6 text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No custom fields yet. Add fields like <span className="text-slate-300">Serial Number</span>,{" "}
                  <span className="text-slate-300">Color</span> or <span className="text-slate-300">Location</span>.
                </p>
              </div>
            )}

            <div className="space-y-3" data-testid="fields-list">
              {config.fields.map((f, idx) => (
                <div
                  key={f.id}
                  data-testid="field-config-row"
                  className={`rounded-xl border p-3 transition-colors ${
                    f.enabled ? "border-border bg-secondary/20" : "border-border/50 bg-secondary/10 opacity-70"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                    <Input
                      data-testid="field-label-input"
                      value={f.label}
                      onChange={(e) => updateField(idx, { label: e.target.value })}
                      placeholder={`Field name (optional → ${labelFor({ label: "" }, idx)})`}
                      className="bg-card border-border font-semibold text-slate-100 h-9"
                    />
                    <Switch
                      data-testid="field-enabled-switch"
                      checked={f.enabled}
                      onCheckedChange={(v) => updateField(idx, { enabled: v })}
                    />
                    <button
                      data-testid="field-delete-button"
                      onClick={() => removeField(idx)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 pl-6">
                    <Input
                      data-testid="field-instruction-input"
                      value={f.ai_instruction}
                      onChange={(e) => updateField(idx, { ai_instruction: e.target.value })}
                      placeholder="AI instruction — e.g. 'serial / model number', 'dominant color'"
                      className="bg-card border-border text-xs h-8"
                    />
                    {!f.enabled && (
                      <Badge variant="outline" className="mt-2 text-[10px] text-muted-foreground border-border">
                        Hidden from AI & table
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Saved to your account automatically</span>
          <Button onClick={() => onOpenChange(false)} className="bg-primary text-primary-foreground hover:bg-primary/90">
            Done
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
